import { Flex, Popup, ProviderConsumer, Text } from '@fluentui/react-northstar';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { appState } from '../../AppState';
import { ScopedInstanceMember } from '../../shared/interfaces/scopeInstanceMember.interface';
import { ScopeMemberAvatar, ScopePersonRow } from './ScopeMemberAvatar';
import { AVATAR_OVERLAP_RATIO, AVATAR_SIZE, DIVIDER_HEIGHT, DIVIDER_WIDTH, fitBuckets, MAX_DISPLAYED, parseMembers } from './availableToWidth';
import { useEntraGroupNames } from './entraGroupNames';
import { BUCKET_ORDER, membersOfBucket, ResolvedScopeMember, resolveScopedMembers, ScopeBucket, ScopeMemberKind } from './scopedMembers';

export interface IAvailableTo {
  // Raw ScopedMembers off the record. Accepts the serialised form too, since the
  // KB list stores it as a JSON string.
  scopedMembers: ScopedInstanceMember[] | string;
  // Avatars shown per bucket before collapsing into "+n".
  maxDisplayed?: number;
  avatarSize?: number;
  // Overrides the default avatar-stack trigger (e.g. a scope indicator icon
  // elsewhere in the UI) while reusing the same hover panel.
  trigger?: React.ReactNode;
  // Width the avatar stacks have to fit into. Buckets past it collapse into a
  // trailing count. Left out, the cell draws every bucket at its natural width.
  maxWidth?: number;
}

// A single overlapping run of avatars for one bucket, followed by its overflow
// count. Each avatar gets a ring in the surrounding background colour so the
// discs stay legible where they overlap.
const AvatarStack = ({ members, maxDisplayed, avatarSize, ringColor }: {
  members: ResolvedScopeMember[];
  maxDisplayed: number;
  avatarSize: number;
  ringColor: string;
}) => {
  const shown = members.slice(0, maxDisplayed);
  const overflow = members.length - shown.length;

  return (
    <Flex vAlign='center' styles={{ flexShrink: 0 }}>
      {shown.map((member, i) => (
        <span
          key={member.key}
          style={{
            display: 'inline-flex',
            marginLeft: i === 0 ? 0 : `-${Math.round(avatarSize * AVATAR_OVERLAP_RATIO)}px`,
            borderRadius: '50%',
            boxShadow: `0 0 0 2px ${ringColor}`,
            zIndex: i + 1,
            lineHeight: 0
          }}
        >
          <ScopeMemberAvatar member={member} size={avatarSize} />
        </span>
      ))}
      {overflow > 0 && <Text size='small' styles={{ marginLeft: '6px' }} content={`+${overflow}`} />}
    </Flex>
  );
};

const Section = ({ title, members, avatarSize, textColor }: {
  title: string;
  members: ResolvedScopeMember[];
  avatarSize: number;
  textColor: string;
}) => {
  if (members.length === 0) return null;

  return (
    <>
      <Text weight='semibold' size='small' styles={{ padding: '8px 12px 4px' }} content={title} />
      {members.map(member => (
        <Flex key={member.key} vAlign='center' gap='gap.smaller' styles={{ padding: '4px 12px' }}>
          {member.kind === ScopeMemberKind.Person ? (
            <ScopePersonRow member={member} size={avatarSize} color={textColor} />
          ) : (
            <>
              <span style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0 }}>
                <ScopeMemberAvatar member={member} size={avatarSize} />
              </span>
              <Text size='small' truncated content={member.name} />
            </>
          )}
        </Flex>
      ))}
    </>
  );
};

// Available To cell: one avatar stack per member type, with a read only hover
// list of everything the record is shared with. #34542
export const AvailableTo = ({ scopedMembers, maxDisplayed = MAX_DISPLAYED, avatarSize = AVATAR_SIZE, trigger, maxWidth }: IAvailableTo) => {
  const { t } = useTranslation();
  const state = appState();

  // Popper measures the panel once, on open. The person rows resolve their names
  // from Graph a moment later and widen it, so by the time the panel is its final
  // size Popper has already decided where to put it and nothing asks it again -
  // leaving it hanging off the right of the screen. Re-run the positioning
  // whenever the panel resizes. #34542
  const popperRef = useRef<any>(null);
  const resizeObserver = useRef<ResizeObserver>(null);

  const observeContent = useCallback((el: HTMLElement | null) => {
    resizeObserver.current?.disconnect();
    resizeObserver.current = null;

    if (!el || typeof ResizeObserver === 'undefined') return;

    resizeObserver.current = new ResizeObserver(() => popperRef.current?.updatePosition());
    resizeObserver.current.observe(el);
  }, []);

  const scoped = resolveScopedMembers(parseMembers(scopedMembers), state);
  const entraGroupNames = useEntraGroupNames(scoped);
  const members = scoped.map(member =>
    member.bucket === ScopeBucket.Entra && member.kind === ScopeMemberKind.Group
      ? { ...member, name: entraGroupNames.get(member.aadObjectId) }
      : member
  );

  // Nothing scoped means sharing was never limited, so every agent can use the
  // record. That is a real state worth naming - a blank cell reads as missing
  // data. There is nothing to list, so no hover panel here. #34542
  // A caller-supplied trigger (e.g. a scope indicator icon) only ever renders
  // once something is actually scoped, so there is nothing meaningful to show
  // instead - just render the icon with no hover panel attached.
  if (members.length === 0) return trigger ? <>{trigger}</> : <Text size='small' content={t('scoping.everyone')} />;

  // One group per control in the sharing picker, same order, same labels - so the
  // read only view reads back the way sharing was set up. #34542
  const titles: Record<string, string> = {
    [ScopeBucket.Entra]: t('scoping.entra-user-group'),
    [ScopeBucket.Team]: t('scoping.teams'),
    [ScopeBucket.Group]: t('scoping.groups'),
    [ScopeBucket.Assignee]: t('scoping.assignees')
  };
  const buckets = BUCKET_ORDER.map(id => ({ id, title: titles[id], members: membersOfBucket(members, id) }))
   .filter(b => b.members.length > 0);

  // In a table the cell has a width to live within, so the stacks that do not fit
  // collapse into one count rather than being clipped away unannounced. The hover
  // panel below is unaffected and still lists every member. #35321
  const { shownBuckets, hiddenMembers } = maxWidth
    ? fitBuckets(buckets.map(b => b.members.length), maxWidth, maxDisplayed, avatarSize)
    : { shownBuckets: buckets.length, hiddenMembers: 0 };

  return (
    <ProviderConsumer
      render={globalTheme => {
        const colors = globalTheme.siteVariables.colorScheme.default;

        return (
          <Popup
            on='hover'
            position='below'
            align='start'
            // Popper clips to the nearest scrolling ancestor by default, so in a
            // table the panel gets cut off at the cell rather than moving. Bound
            // it to the viewport instead, and position fixed so an overflow:hidden
            // ancestor cannot crop it.
            flipBoundary='window'
            overflowBoundary='window'
            positionFixed
            // overflowBoundary only sets preventOverflow's boundary - it leaves
            // altAxis at Popper's default of false. For a 'below' placement the
            // main axis is vertical, so without altAxis the panel is free to run
            // off the right edge of the screen and never shifts back. Passing
            // 'all' here is what turns altAxis on. #34542
            unstable_disableTether='all'
            popperRef={popperRef}
            contentRef={observeContent}
            trigger={
              trigger ??
              <Flex
                gap='gap.small'
                vAlign='center'
                styles={{ cursor: 'default', width: 'fit-content' }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {buckets.slice(0, shownBuckets).map(bucket => (
                  <AvatarStack
                    key={bucket.id}
                    members={bucket.members}
                    maxDisplayed={maxDisplayed}
                    avatarSize={avatarSize}
                    ringColor={colors.background}
                  />
                ))}
                {hiddenMembers > 0 && (
                  <>
                    <span
                      aria-hidden='true'
                      style={{
                        width: `${DIVIDER_WIDTH}px`,
                        height: `${DIVIDER_HEIGHT}px`,
                        backgroundColor: colors.border,
                        flexShrink: 0
                      }}
                    />
                    <Text size='small' content={`+${hiddenMembers}`} />
                  </>
                )}
              </Flex>
            }
            content={{
              styles: {
                padding: 0,
                width: 'max-content',
                maxWidth: 'calc(100vw - 32px)'
              },
              content: (
                <Flex
                  column
                  styles={{
                    width: 'max-content',
                    minWidth: '164px',
                    maxWidth: 'calc(100vw - 32px)',
                    maxHeight: '320px',
                    overflowY: 'auto',
                    padding: '4px 0'
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {buckets.map(bucket => (
                    <Section
                      key={bucket.id}
                      title={bucket.title}
                      members={bucket.members}
                      avatarSize={avatarSize}
                      textColor={colors.foreground}
                    />
                  ))}
                </Flex>
              )
            }}
          />
        );
      }}
    />
  );
};
